import { createContext, useContext, useState, useEffect } from 'react';

const CART_KEY = 'cart';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

function loadCart() {
  try {
    const saved = localStorage.getItem(CART_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    localStorage.removeItem(CART_KEY);
  }
  return [];
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    const normalizedQty = Math.max(1, Number(quantity) || 1);
    const item = {
      id: product.id,
      name: product.name,
      price: product.price,
      branch_id: product.branch_id,
      quantity: normalizedQty,
      photo_url: product.photo_url ?? null,
      category: product.category ?? null,
    };
    setCart((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        return prev.map((p) =>
          p.id === product.id
            ? { ...p, quantity: (p.quantity || 1) + normalizedQty, photo_url: p.photo_url ?? product.photo_url ?? null, category: p.category ?? product.category ?? null }
            : p
        );
      }
      return [...prev, item];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart((prev) => {
      const item = prev.find((p) => p.id === productId);
      if (!item) return prev;
      const nextQty = Math.max(0, (item.quantity || 1) + delta);
      if (nextQty === 0) return prev.filter((p) => p.id !== productId);
      return prev.map((p) =>
        p.id === productId ? { ...p, quantity: nextQty } : p
      );
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((p) => p.id !== productId));
  };

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);

  return (
    <CartContext.Provider
      value={{ cart, cartCount, addToCart, updateQuantity, removeFromCart, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};
